import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import cn from "classnames";
import { toast } from "react-toastify";
import style from "../common.module.scss";
import { File } from "../../../../services";
import CloseIconNew from "../../../shared/icons/CloseIconNew";
import EyeIcon from "../../../shared/icons/EyeIcon";
import Loading from "../../../shared/Loading/Loading";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import DocumentNewIcon from "../../../shared/icons/DocumentNewIcon";
import PhotoViewer from "../../../partners/components/PhotoViewer";
import UnloadIcon from "../../../shared/icons/UnloadIcon";
import CustomButton from "../../../custom/custom-button/CustomButton";
import ConfirmModal from "../../../shared/ConfirmModal/ConfirmModal";

const formatDocumentType = (contentType) => {
  // Keep this function as is, it deals with mime types, not user-facing text
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  return mimeMap[contentType] || contentType?.split("/")[1];
};

const DriverDocumentViewer = ({ setShowModal, documents, orderId = "" }) => {
  const { t } = useTranslation(); // Initialize t function
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [file, setFile] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState();
  const [loadingStatesDownload, setLoadingStatesDownload] = useState({});
  const [currentDocType, setCurrentDocType] = useState("");
  const [cachedFiles, setCachedFiles] = useState({});
  const [showConfirmDownloadModal, setShowConfirmDownloadModal] = useState(false);

  const modalRef = useRef(null);

  useClickOutside(modalRef, () => {
    setShowModal(false);
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const setLoadingViewer = (docId, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const setLoadingDownload = (docId, isLoading) => {
    setLoadingStatesDownload((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const openViewPhoto = (fileId) => {
    if (cachedFiles[fileId]) {
      const file = cachedFiles[fileId];
      setFile({ file: file.blob, type: file.type });
      setCurrentDocType(fileId);

      if (file.type === "application/pdf") {
        window.open(file.url, "_blank");
      } else {
        setImgUrl(file.url);
        setIsOpen(true);
      }
      return;
    }

    setIsLoading(true);
    setFile(null);
    setLoadingViewer(fileId, true);

    File.getDocById(fileId)
      .then((response) => {
        setIsLoading(false);
        setLoadingViewer(fileId, false);
        const file = new Blob([response.data], {
          type: response.data.type,
        });

        setFile({ file: file, type: response.data.type });
        const fileURL = URL.createObjectURL(file);
        setCachedFiles((prev) => ({
          ...prev,
          [fileId]: { blob: file, url: fileURL, type: response.data.type },
        }));
        setCurrentDocType(fileId);

        if (response.data.type === "application/pdf") {
          window.open(fileURL, "_blank");
        } else {
          setImgUrl(fileURL);
          setIsOpen(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setLoadingViewer(fileId, false);
        console.log("Произошла ошибка, попробуйте позже", error); // Keep console log
        alert(t('orderInfo.genericError')); // Use alert with translation
      });
  };

  const onDownload = (fileId) => {
    const doc = documents.find((x) => x.fileId === fileId);
    if (!doc) {
      alert(t('orderInfo.driverDocumentViewer.docNotFound')); // Use alert with translation
      return;
    }

    if (cachedFiles[doc.fileId]) {
      const file = cachedFiles[doc.fileId];
      const downloadElement = document.createElement("a");
      downloadElement.href = file.url;
      downloadElement.download = `driversByOrderDoc.${formatDocumentType(file.type)}`;
      downloadElement.click();
      setIsOpen(false);
      return;
    }

    setLoadingDownload(fileId, true);
    File.getDocById(doc.fileId)
      .then((response) => {
        const fileBlob = new Blob([response.data], {
          type: response.data.type,
        });

        const fileURL = URL.createObjectURL(fileBlob);

        setCachedFiles((prev) => ({
          ...prev,
          [doc.fileId]: { blob: fileBlob, url: fileURL, type: response.data.type },
        }));

        const downloadElement = document.createElement("a");
        downloadElement.href = fileURL;
        downloadElement.download = `driversByOrderCount.${formatDocumentType(response.data.type)}`;
        downloadElement.click();
      })
      .catch((error) => {
        console.log("Произошла ошибка, попробуйте позже", error); // Keep console log
        alert(t('orderInfo.genericError')); // Use alert with translation
      })
      .finally(() => {
        setLoadingDownload(fileId, false);
        setIsOpen(false);
      });
  };

  const handleDownloadButtonClick = () => {
    setShowConfirmDownloadModal(true);
  };

  const handleConfirmDownload = () => {
    if (!orderId) {
      toast.error(t('orderInfo.genericError'));
      setShowConfirmDownloadModal(false);
      return;
    }

    setIsLoading(true);
    File.downloadOrderZipDriver(orderId)
      .then((response) => {
        const fileBlob = new Blob([response.data], {
          type: 'application/zip',
        });

        const fileURL = URL.createObjectURL(fileBlob);
        const downloadElement = document.createElement("a");
        downloadElement.href = fileURL;
        downloadElement.download = `order_${orderId}_documents.zip`;
        downloadElement.click();
        URL.revokeObjectURL(fileURL);
        setShowConfirmDownloadModal(false);
      })
      .catch((error) => {
        console.log("Произошла ошибка при скачивании архива", error);
        toast.error(t('orderInfo.genericError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancelDownload = () => {
    setShowConfirmDownloadModal(false);
  };

  return (
    <>
      <div className={style.modalOverlay}>
        <div className={style.modalContent} style={{ gap: "8px" }}>
          <h4>
            {t('orderInfo.driverDocumentViewer.title')}
            <CloseIconNew
              onClick={() => setShowModal(false)}
              style={{ cursor: "pointer" }}
            />
          </h4>
          {documents && documents.length > 0 ? (
            <div className={style.documentsList}>
              {documents.map((doc, index) => (
                <div key={index} className={style.documentItem}>
                  <div className={style.documentItem_icon}>
                    <DocumentNewIcon />
                  </div>
                  <div className={style.documentItem_content}>
                    <div className={style.documentItem_name}>
                      {doc?.typeName}.{formatDocumentType(doc?.contentType)}
                    </div>
                    <div className={style.documentItem_size}>{doc?.sideName}</div>
                  </div>

                  {loadingStatesDownload[doc?.fileId] ? (
                    <Loading inBlock marginBottom={0} />
                  ) : (
                    <UnloadIcon
                      onClick={() => {
                        onDownload(doc.fileId);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  )}

                  {loadingStates[doc?.fileId] ? (
                    <Loading inBlock marginBottom={0} />
                  ) : (
                    <EyeIcon
                      onClick={() => {
                        openViewPhoto(doc?.fileId);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                </div>
              ))}
              <CustomButton
                text={t("orderInfo.orderHistory.downloadAll")}
                onClick={handleDownloadButtonClick}
                isFall
                disabled={isLoading}
                isLoading={isLoading}
              />
            </div>
          ) : (
            <span>{t('orderInfo.driverDocumentViewer.noDocuments')}</span>
          )}
        </div>
      </div>

      {isOpen && (
        <PhotoViewer
          imgUrl={imgUrl}
          onClose={handleClose}
          onDownload={() => onDownload(currentDocType)}
          ShowBtnDownload
        />
      )}

      <ConfirmModal
        isOpen={showConfirmDownloadModal}
        title={t("orderInfo.orderHistory.downloadAllTitle")}
        message={t("orderInfo.orderHistory.downloadAllMessage")}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        onConfirm={handleConfirmDownload}
        onCancel={handleCancelDownload}
        isLoading={isLoading}
      />
    </>
  );
};

export default DriverDocumentViewer;
